import { useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { productShape } from "app/utils/propTypes";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import {
	FLAGSHIP_EVENT_KPIS,
	OFFER_CONTRACT_TYPES,
	PRODUCT_EXPERIENCES_WITH_HOTEL,
} from "app/constants";
import ProductTopBar from "@brand/Product/ProductTopBar";
import BadgeList from "app/pages/.shared/ColoredProductBadge/BadgeList";
import { pushTagOnProductViewButtonClick } from "app/utils/analytics";
import "./ProductOriginal.scss";
import ProductDestinationTitleContainer from "app/pages/.shared/ProductDestinationTitle/ProductDestinationTitleContainer";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import ProductLinkContainer from "app/pages/.shared/ProductLink/ProductLinkContainer";
import IconRight from "app/pages/.shared/static/icons/IconRight";
import { containsTrainTransportationTypes } from "app/utils/utils";
import ProductDeal from "app/pages/.shared/Deal/ProductDeal";
import { EventCategory, HitType, useFlagship, useFsFlag } from "@flagship.io/react-sdk";
import { useLocation } from "react-router-dom";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";

import ImagesCarouselPP from "app/pages/SmartDP/Listing/SmartDPProduct/ImagesCarousel/ImagesCarouselPP";
import ImageWithoutCarousel from "app/pages/SmartDP/Listing/SmartDPProduct/ImagesCarousel/ImageWithoutCarousel";

const IMG_SIZES = {
	small: { width: 768, height: 416 },
	medium: { width: 768, height: 416 },
	large: { width: 502, height: 240 },
	xlarge: { width: 502, height: 240 },
};

const ProductCardComponent = ({
	position,
	resolution,
	product = {},
	travelByTrainAvailable,
	showSmartDPOnRootUrl,
	displayCardSliderOnMob = true,
}) => {
	const { pathname } = useLocation();
	const { hit: fsHit } = useFlagship();

	const handleProductClick = useCallback(() => {
		fsHit.send({
			type: HitType.EVENT,
			category: EventCategory.ACTION_TRACKING,
			action: FLAGSHIP_EVENT_KPIS.CLICK_ON_PP_PRODUCT_CARD,
			label: pathname,
		});
		pushTagOnProductViewButtonClick(product, position + 1);
	}, [product.uri, position, pathname]);

	const productBadgesShow = useFsFlag("product_badges_show", true);
	const isHomePage = pathname.includes("/home");
	const productBadgesShowValue = productBadgesShow.getValue(false);

	if (!isHomePage) {
		productBadgesShow.userExposed();
	}

	const openInNewTab = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	return (
		<div className="product" data-testid="product">
			<div className="product__picture">
				{!displayCardSliderOnMob ? (
					<ImageWithoutCarousel
						handleProductClick={handleProductClick}
						product={product}
						openInNewTab={openInNewTab}
						resolution={resolution}
					/>
				) : (
					<ImagesCarouselPP
						sizes={IMG_SIZES}
						images={product?.photos}
						alt={product?.name}
						productUri={product?.uri}
						handleProductClick={handleProductClick}
						openInNewTab={openInNewTab}
					/>
				)}

				<div className="product__topbar">
					<ProductTopBar
						isFlashsale={product?.isFlashsale}
						productExpirationHours={product?.expiration?.hour}
						productExpirationDays={product?.expiration?.day}
						showSmartDPOnRootUrl={showSmartDPOnRootUrl}
					/>
				</div>
			</div>
			<div className="product__deal">
				<ProductDeal
					travelByTrainAvailable={travelByTrainAvailable}
					isRental={product?.contractType === OFFER_CONTRACT_TYPES.RENTAL}
					includeOfferWithFlight={product?.includeOfferWithFlight}
					duration={product?.fromPriceType?.numberOfNights}
					publicPrice={product?.fromPriceType?.publicPrice}
					price={product?.fromPriceType?.price}
					hour={product?.expiration?.hour}
					day={product?.expiration?.day}
					type={product?.fromPriceType?.type}
					value={product?.fromPriceType?.value}
					isWithHotel={PRODUCT_EXPERIENCES_WITH_HOTEL.includes(
						product?.productExperience
					)}
				/>
			</div>
			<div className="product__footer">
				<div className="product__info product__info--left">
					<div className="product__main">
						<ProductDestinationTitleContainer
							country={product.country}
							region={product.region}
							resort={product.resort}
						/>
						<ProductNameWithCategory
							categoryType={product.categoryType}
							category={product.category}
							name={product.name}
						/>
					</div>

					<ProductRatingContainer
						tripAdvisor={product.tripadvisor || {}}
						holidayCheck={{
							...(product.holidaycheck || {}),
							size: "small",
						}}
						isCompact={isMobile}
					/>

					{product?.badges?.length > 0 && productBadgesShowValue && (
						<div className="product__badges">
							<BadgeList badges={product.badges} />
						</div>
					)}
				</div>

				<div className="product__info product__info--right">
					<div className="product__action">
						<ProductLinkContainer
							handleProductClick={handleProductClick}
							productUri={product.uri}
							openOnNewTab={openInNewTab}
						>
							<IconRight width={20} height={20} />
						</ProductLinkContainer>
					</div>
				</div>
			</div>
		</div>
	);
};

ProductCardComponent.propTypes = {
	position: PropTypes.number,
	product: productShape,
	resolution: PropTypes.string,
	travelByTrainAvailable: PropTypes.bool,
	showSmartDPOnRootUrl: PropTypes.bool,
	displayCardSliderOnMob: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => {
	return {
		resolution: state.resolution,
		travelByTrainAvailable: containsTrainTransportationTypes(
			ownProps.product.transportationTypes
		),
	};
};

export const ProductOriginal = connect(mapStateToProps)(ProductCardComponent);
